import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  LogIn,
  Shield,
  MessageSquare,
  Sparkles,
  FileText,
  Headphones,
  FolderOpen,
  CreditCard,
  Bell,
  BarChart,
  Eye,
  EyeOff,
  AlertCircle,
  ArrowRight,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { DynamicLogo } from '../../components/DynamicLogo';
import publicApi from '../../services/publicApi';

const portalFeatures = [
  {
    icon: Headphones,
    title: 'Suporte Técnico',
    description: 'Abra tickets e acompanhe suas solicitações em tempo real',
  },
  {
    icon: FolderOpen,
    title: 'Meus Projetos',
    description: 'Acesse documentação, status e informações dos seus projetos',
  },
  {
    icon: CreditCard,
    title: 'Faturas',
    description: 'Consulte histórico de pagamentos e faturas pendentes',
  },
  {
    icon: BarChart,
    title: 'Relatórios',
    description: 'Visualize métricas de performance e uptime dos serviços',
  },
  {
    icon: FileText,
    title: 'Base de Conhecimento',
    description: 'Tutoriais, guias e documentação técnica',
  },
  {
    icon: Bell,
    title: 'Notificações',
    description: 'Fique por dentro de atualizações e manutenções',
  },
];

export function ClientArea() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [supportPhone, setSupportPhone] = useState<string | null>(null);
  const { login, logout, isLoading, error, isAuthenticated, isAdmin, clearError } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      if (isAdmin) {
        logout();
        setLoginError('Use a Área Administrativa para acessar o painel de gestão');
      } else {
        window.location.href = '/portal';
      }
    }
  }, [isAuthenticated, isAdmin, logout]);

  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  useEffect(() => {
    const loadSupportPhone = async () => {
      try {
        const response = await publicApi.get('/theme');
        setSupportPhone(response.data?.telefone || null);
      } catch {
        setSupportPhone(null);
      }
    };

    loadSupportPhone();
  }, []);

  const supportHref = (() => {
    const numericPhone = supportPhone?.replace(/\D/g, '');
    if (!numericPhone) return null;
    const normalized = numericPhone.startsWith('55') ? numericPhone : `55${numericPhone}`;
    return `https://wa.me/${normalized}`;
  })();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);

    try {
      const result = await login(email, password);
      if (result?.user?.role === 'admin') {
        logout();
        setLoginError('Use a Área Administrativa para acessar o painel de gestão');
      } else {
        window.location.href = '/portal';
      }
    } catch {
      // Erro já tratado no store
    }
  };

  const displayError = loginError || error;

  return (
    <>
      <Helmet>
        <title>Portal do Cliente</title>
        <meta
          name="description"
          content="Acesse sua área exclusiva para gerenciar projetos, abrir tickets de suporte e muito mais."
        />
      </Helmet>

      <div className="min-h-screen bg-background relative overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-64 h-64 bg-primary/20 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
        </div>

        {/* Vertical lines pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border)/0.3)_1px,transparent_1px)] bg-[size:8rem_100%] pointer-events-none" style={{ maskImage: 'radial-gradient(ellipse 80% 50% at 50% 0%, black 70%, transparent 110%), linear-gradient(to bottom, black 0%, black 85%, transparent 100%)', maskComposite: 'intersect', WebkitMaskImage: 'radial-gradient(ellipse 80% 50% at 50% 0%, black 70%, transparent 110%), linear-gradient(to bottom, black 0%, black 85%, transparent 100%)', WebkitMaskComposite: 'source-in' }} />

        {/* Header */}
        <header className="border-b border-border/50 bg-card/30 backdrop-blur-xl sticky top-0 z-50 relative">
          <div className="container mx-auto px-4 py-4 flex justify-between items-center">
            <Link to="/">
              <DynamicLogo
                area="header"
                alt="Portal"
                className="h-11 w-auto"
                fallback={
                  <div className="font-bold text-primary text-xl">Portal</div>
                }
              />
            </Link>
            <nav className="hidden md:flex gap-6">
              <Link to="/#servicos" className="text-muted-foreground hover:text-primary transition-colors">
                Serviços
              </Link>
              <Link to="/blog" className="text-muted-foreground hover:text-primary transition-colors">
                Blog
              </Link>
              <Link to="/contato" className="text-muted-foreground hover:text-primary transition-colors">
                Contato
              </Link>
            </nav>
            <button className="border border-primary/50 text-primary hover:bg-primary/10 hover:border-primary px-4 py-2 rounded-lg transition-colors">
              <Link to="/admin/login">Área Administrativa</Link>
            </button>
          </div>
        </header>

        {/* Hero Section */}
        <section className="py-20 md:py-32 px-4 relative">
          <div className="container mx-auto relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Side - Info */}
              <div className="space-y-8">
                <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
                  <ArrowRight className="h-4 w-4 rotate-180" />
                  Voltar para Home
                </Link>

                <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-6 backdrop-blur-sm">
                  <Sparkles className="h-4 w-4 text-primary animate-pulse" />
                  <span className="text-sm font-medium text-primary">Área Exclusiva</span>
                </div>

                <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">
                  Portal do{" "}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-accent to-primary bg-[length:200%_auto] animate-[gradient_3s_linear_infinite]">
                    Cliente
                  </span>
                </h1>

                <p className="text-xl text-muted-foreground mb-8">
                  Acesse sua área exclusiva para gerenciar projetos, abrir tickets de suporte e muito mais.
                </p>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Shield className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">Acesso Seguro</h3>
                      <p className="text-sm text-muted-foreground">Seus dados protegidos com criptografia de ponta a ponta</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MessageSquare className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">Suporte 24/7</h3>
                      <p className="text-sm text-muted-foreground">Equipe disponível para atender suas necessidades</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Side - Login Form */}
              <div className="border border-border/50 bg-card/50 backdrop-blur-xl rounded-xl p-8">
                <div className="text-center mb-6">
                  <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center mx-auto mb-4">
                    <LogIn className="h-8 w-8 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold text-foreground">Acesse sua conta</h2>
                  <p className="text-muted-foreground mt-2">
                    Entre com suas credenciais para acessar o portal
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {displayError && (
                    <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/30 flex items-center gap-2 text-destructive">
                      <AlertCircle className="h-4 w-4" />
                      <span className="text-sm">{displayError}</span>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-medium text-foreground">Email</label>
                    <input
                      id="email"
                      type="email"
                      placeholder="seu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={isLoading}
                      className="w-full px-3 py-2 bg-background/50 border border-border/50 focus:border-primary transition-colors rounded-lg text-foreground placeholder:text-muted-foreground"
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label htmlFor="password" className="text-sm font-medium text-foreground">Senha</label>
                      <Link to="/recuperar-senha" className="text-sm text-primary hover:underline">
                        Esqueceu a senha?
                      </Link>
                    </div>
                    <div className="relative">
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        disabled={isLoading}
                        className="w-full px-3 py-2 bg-background/50 border border-border/50 focus:border-primary transition-colors rounded-lg text-foreground placeholder:text-muted-foreground pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 group"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <div className="h-4 w-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                        Entrando...
                      </>
                    ) : (
                      <>
                        Entrar
                        <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </button>

                  <div className="text-center text-sm text-muted-foreground">
                    Não tem acesso?{" "}
                    <Link to="/contato" className="text-primary hover:underline">
                      Entre em contato
                    </Link>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-20 px-4 relative bg-card/30">
          <div className="container mx-auto relative z-10">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                O que você encontra no portal
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Tudo que você precisa em um só lugar
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {portalFeatures.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={index}
                    className="border border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 group rounded-xl p-6"
                  >
                    <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">
                      {feature.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 px-4 relative">
          <div className="container mx-auto relative z-10 max-w-4xl">
            <div className="bg-gradient-to-br from-primary/20 via-card/50 to-accent/20 border border-primary/30 backdrop-blur-xl overflow-hidden relative group rounded-xl p-8">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/0 via-primary/10 to-primary/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
              <div className="text-center relative z-10">
                <h3 className="text-3xl md:text-4xl mb-4 text-foreground font-bold">
                  Precisa de ajuda?
                </h3>
                <p className="text-foreground/80 text-lg mb-6">
                  Nossa equipe está pronta para atender você
                </p>
                {supportHref && (
                  <a
                    href={supportHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 border border-primary/50 hover:bg-primary/10 text-primary px-6 py-3 rounded-lg font-medium transition-colors"
                  >
                    <MessageSquare className="h-5 w-5" />
                    Falar com Suporte
                  </a>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
